import { useRef, useState, type ReactElement } from 'react';
import { useTranslation } from 'next-i18next';
import { useRouter } from 'next/router';

import styles from './palmares-row.module.scss';
import { StagesTable } from './stages-table/stages-table';
import { ScoreLegend } from '@utmb/design-system/index/score-legend/score-legend';
import { Font } from '@utmb/design-system/text/font';
import { PiCategoryLogo } from '@utmb/design-system/pi-category-logo/PiCategoryLogo';
import { Flag } from '@utmb/design-system/flag/Flag';
import { IconsEnum } from '@utmb/types/icons';
import { Icon } from '@utmb/design-system/icons/Icon';
import { Link } from '@utmb/design-system/link/link';
import { HelpButton } from '@utmb/design-system/help-button/HelpButton';
import { useUser } from '@utmb/contexts/AuthContext';
import { usePreference } from '@utmb/contexts/PreferenceContext';
import { formatUnit } from '@utmb/utils/unit';
import { VALID_INDEX_THRESHOLD } from '@utmb/utils/utmb-index.constants';
import RaceYearResultForRunnerView from '@utmb/types/view/RaceYearResultForRunnerView';
import { RaceTypeEnum } from '@utmb/types/enum/RaceTypeEnum';
import { getColorStyle } from '@utmb/utils/colors';
import { ColorTypesEnum } from '@utmb/types/colors';

export interface PalmaresRowProps {
    result: RaceYearResultForRunnerView;
    isAuthenticated: boolean;
    isMe?: boolean;
    onDelete?: (uri: string) => void;
    showYear?: boolean;
}

export function PalmaresRow({ result, isAuthenticated, isMe = false, onDelete, showYear = false }: PalmaresRowProps): ReactElement {
    const [isExpanded, setIsExpanded] = useState(false);
    // Mobile-only popover for the "X Stages" detail (the desktop badge reveals it on hover).
    const [stagesPopoverOpen, setStagesPopoverOpen] = useState(false);
    // The popover is position: absolute against .palmaresRow (its containing block on
    // mobile); we anchor it just below the "Type" detail line by measuring that cell's
    // offset within the row.
    const raceTypeCellRef = useRef<HTMLDivElement>(null);
    const [stagesPos, setStagesPos] = useState<{ top: number; left: number } | null>(null);

    const openStagesPopover = (): void => {
        const el = raceTypeCellRef.current;
        if (el) setStagesPos({ top: el.offsetTop + el.offsetHeight + 8, left: el.offsetLeft });
        setStagesPopoverOpen(true);
    };
    const { t } = useTranslation();
    const { login } = useUser();
    const { locale } = useRouter();
    const {
        preferences: { preferredUnit },
    } = usePreference();

    const dateLabel = new Date(result.dateIso).toLocaleDateString(locale, { day: 'numeric', month: 'long' });

    const canDelete = isMe && !!onDelete;

    const getPodiumIcon = (rank: number): IconsEnum | null => {
        if (rank === 1) return IconsEnum.TOP1;
        if (rank === 2) return IconsEnum.TOP2;
        if (rank === 3) return IconsEnum.TOP3;
        return null;
    };
    const scratchPodium = !result.isDnf ? getPodiumIcon(result.rank) : null;
    const genderPodium = !result.isDnf ? getPodiumIcon(result.rankGender) : null;

    // Show a race type tag only when the race is something other than a solo race (relay, team).
    const showRaceTypeTag = !!result.raceType && result.raceType !== RaceTypeEnum.SOLO;
    // Stage races (course à étapes) are an orthogonal dimension to participation type — flag them separately.
    const stages = result.stages ?? [];
    const stagesCount = stages.length;
    const showStagesTag = stagesCount > 0;

    // "?" next to a score that isn't a raw number (pending, finisher, not scored).
    const scoreHelpButton = (
        <HelpButton ariaLabel="Aide" className={styles.scoreHelpButton}>
            <ScoreLegend />
        </HelpButton>
    );

    return (
        <div
            className={`${styles.palmaresRow} ${canDelete ? '' : styles.noDelete} ${isAuthenticated ? '' : styles.notAuthenticated} ${
                isExpanded ? styles.expanded : ''
            } ${stagesPopoverOpen ? styles.stagesOpen : ''}`}
        >
            <div className={styles.mainCells}>
                <div className={styles.date}>
                    <Font.FuturaMedium desktop="20" mobile="12">
                        {dateLabel}
                    </Font.FuturaMedium>
                    {showYear && (
                        <Font.FuturaBook className={styles.year} desktop="18" mobile="12">
                            {result.eventYear}
                        </Font.FuturaBook>
                    )}
                </div>
                <Link
                    className={styles.race + ' ' + (result.piCategory ? '' : styles.noCategoryRace)}
                    href={`/utmb-index/races/${result.uri}`}
                    prefetch={false}
                >
                    <div className={styles.wrapCategoryName}>
                        <PiCategoryLogo height={16} piCategory={result.piCategory} isDark={false} />
                        {showRaceTypeTag && (
                            <div className={styles.raceTypeTag}>
                                <Font.FuturaMedium desktop="12" mobile="12" color={getColorStyle(ColorTypesEnum.WS_PRIMARY)}>
                                    {t(`singleRunner.resultsTable.raceType.${result.raceType}`)}
                                </Font.FuturaMedium>
                            </div>
                        )}
                        {showStagesTag && (
                            <span className={`${styles.raceTypeTag} ${styles.stagesTag}`}>
                                <Font.FuturaMedium desktop="12" mobile="12" color={getColorStyle(ColorTypesEnum.WS_PRIMARY)}>
                                    {`${stagesCount} ${t('singleRunner.resultsTable.raceType.stage')}`}
                                </Font.FuturaMedium>
                                {/* Desktop hover tooltip with the stages detail. */}
                                <span className={styles.stagesTooltip} role="tooltip">
                                    <StagesTable stages={stages} />
                                </span>
                            </span>
                        )}
                        <Font.FuturaMedium className={styles.name} desktop="20" mobile="14">
                            {result.raceName}
                        </Font.FuturaMedium>
                    </div>
                    <div className={styles.wrapCountryEvent}>
                        <Flag isoCode={result.countryCode} className={styles.flag} />
                        <Font.FuturaBook className={styles.event} desktop="18" mobile="12">
                            {result.eventName}
                        </Font.FuturaBook>
                    </div>
                </Link>
                <div className={styles.score}>
                    {isAuthenticated ? (
                        result.isDnf ? (
                            <Font.OxaniumMedium className={styles.scoreDash} desktop="40" mobile="18">
                                <span className={styles.scoreDashDesktop}>-</span>
                                <span className={styles.scoreDashMobile}>DNF</span>
                            </Font.OxaniumMedium>
                        ) : result.isCotationPending && result.hasCotation ? (
                            <div className={styles.wrapScoreHelp}>
                                <div className={styles.wrapIcon}>
                                    <Icon variant={IconsEnum.HOURGLASS} width={22} height={22} />
                                    <Font.OxaniumLight className={styles.iconLabel} desktop="14" mobile="12">
                                        {t('singleRunner.resultsTable.indexWaiting')}
                                    </Font.OxaniumLight>
                                </div>
                                {scoreHelpButton}
                            </div>
                        ) : (
                            <>
                                {result.index > VALID_INDEX_THRESHOLD ? (
                                    <Font.OxaniumMedium desktop="40" mobile="18">
                                        {result.index}
                                    </Font.OxaniumMedium>
                                ) : (
                                    <div className={styles.wrapScoreHelp}>
                                        {result.index ? (
                                            <Font.FuturaMedium className={styles.finisher} desktop="16" mobile="10">
                                                {'Finisher'}
                                            </Font.FuturaMedium>
                                        ) : (
                                            <div className={styles.wrapIcon}>
                                                <Icon variant={IconsEnum.BAN} width={22} height={22} />
                                                <Font.OxaniumLight className={styles.iconLabel} desktop="14" mobile="12">
                                                    {t('singleRunner.resultsTable.noCotation')}
                                                </Font.OxaniumLight>
                                            </div>
                                        )}
                                        {scoreHelpButton}
                                    </div>
                                )}
                            </>
                        )
                    ) : (
                        <button type="button" className={styles.loginButton} onClick={login}>
                            <Font.OxaniumRegular desktop="14" mobile="12">
                                {t('singleRunner.resultsTable.connectSeeIndex')}
                            </Font.OxaniumRegular>
                        </button>
                    )}
                </div>
                <button
                    type="button"
                    className={styles.toggleExpand}
                    onClick={() =>
                        setIsExpanded((v) => {
                            if (v) setStagesPopoverOpen(false);
                            return !v;
                        })
                    }
                    aria-expanded={isExpanded}
                    aria-label={isExpanded ? 'Réduire' : 'Voir plus'}
                >
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                        <circle cx="5" cy="12" r="2" />
                        <circle cx="12" cy="12" r="2" />
                        <circle cx="19" cy="12" r="2" />
                    </svg>
                </button>
            </div>

            <div className={styles.detailCells}>
                <div className={styles.category}>
                    <Font.FuturaBook className={styles.mobileLabel} desktop="14" mobile="12">
                        {t('singleRunner.resultsTable.category')}
                    </Font.FuturaBook>
                    <PiCategoryLogo height={16} piCategory={result.piCategory} isDark={isExpanded} />
                </div>
                <div className={styles.country}>
                    <Font.FuturaBook className={styles.mobileLabel} desktop="14" mobile="12">
                        {t('common.country')}
                    </Font.FuturaBook>
                    <Flag isoCode={result.countryCode} className={styles.flag} />
                </div>
                {(showRaceTypeTag || showStagesTag) && (
                    <div className={styles.raceType} ref={raceTypeCellRef}>
                        <Font.FuturaBook className={styles.mobileLabel} desktop="14" mobile="12">
                            {t('singleRunner.resultsTable.type')}
                        </Font.FuturaBook>
                        <div className={styles.raceTypeValue}>
                            {showRaceTypeTag && (
                                <Font.FuturaMedium as="span" desktop="20" mobile="14">
                                    {t(`singleRunner.resultsTable.raceType.${result.raceType}`)}
                                </Font.FuturaMedium>
                            )}
                            {showRaceTypeTag && showStagesTag && <span className={styles.raceTypeSep}>·</span>}
                            {showStagesTag && (
                                <button
                                    type="button"
                                    className={styles.stagesButton}
                                    onClick={() => (stagesPopoverOpen ? setStagesPopoverOpen(false) : openStagesPopover())}
                                    aria-haspopup="dialog"
                                    aria-expanded={stagesPopoverOpen}
                                >
                                    <Font.FuturaMedium as="span" desktop="20" mobile="14">
                                        {`${stagesCount} ${t('singleRunner.resultsTable.raceType.stage')}`}
                                    </Font.FuturaMedium>
                                </button>
                            )}
                        </div>
                    </div>
                )}
                <div className={styles.metrics}>
                    <Font.FuturaBook className={styles.mobileLabel} desktop="14" mobile="12">
                        {t('singleRunner.resultsTable.distance')}
                    </Font.FuturaBook>
                    <div className={styles.metricsValues}>
                        <Font.FuturaMedium className={styles.distance} desktop="20" mobile="16">
                            {result.distance ? formatUnit(preferredUnit, 'distance', Number(result.distance)) : ''}
                        </Font.FuturaMedium>
                        <div className={styles.divider} />
                        <Font.FuturaBook className={styles.elevation} desktop="18" mobile="14">
                            {result.elevationGain ? formatUnit(preferredUnit, 'elevationGain', Number(result.elevationGain)) : ''}
                        </Font.FuturaBook>
                    </div>
                </div>
                <div className={styles.time}>
                    <Font.FuturaBook className={styles.mobileLabel} desktop="14" mobile="12">
                        {t('singleRunner.resultsTable.time')}
                    </Font.FuturaBook>
                    <Font.FuturaMedium className={result.isDnf ? styles.timeDash : ''} desktop="20" mobile="16">
                        {result.isDnf ? '-' : result.time}
                    </Font.FuturaMedium>
                </div>
                <div className={styles.scratch}>
                    <Font.FuturaBook className={styles.mobileLabel} desktop="14" mobile="12">
                        {t('singleRunner.resultsTable.rank')}
                    </Font.FuturaBook>
                    {result.isDnf ? (
                        <Font.FuturaMedium className={styles.rank} desktop="20" mobile="14">
                            DNF
                        </Font.FuturaMedium>
                    ) : (
                        <>
                            {scratchPodium && (
                                <span className={styles.podiumMedal}>
                                    <Icon variant={scratchPodium} width={18} height={18} hideText />
                                </span>
                            )}
                            <Font.FuturaMedium className={styles.rank} desktop="20" mobile="14">
                                {result.rank}
                            </Font.FuturaMedium>
                            <div className={styles.divider} />
                            <Font.FuturaMedium className={styles.scale} desktop="20" mobile="14">
                                {result.totalRanked}
                            </Font.FuturaMedium>
                        </>
                    )}
                </div>
                <div className={styles.gender}>
                    <Font.FuturaBook className={styles.mobileLabel} desktop="14" mobile="12">
                        {t('singleRunner.resultsTable.rankGender')}
                    </Font.FuturaBook>
                    {result.isDnf ? (
                        <Font.FuturaMedium className={styles.rank} desktop="20" mobile="14">
                            DNF
                        </Font.FuturaMedium>
                    ) : (
                        <>
                            {genderPodium && (
                                <span className={styles.podiumMedal}>
                                    <Icon variant={genderPodium} width={18} height={18} hideText />
                                </span>
                            )}
                            <Font.FuturaMedium className={styles.rank} desktop="20" mobile="14">
                                {result.rankGender}
                            </Font.FuturaMedium>
                            <div className={styles.divider} />
                            <Font.FuturaMedium className={styles.scale} desktop="20" mobile="14">
                                {result.totalRankedGender}
                            </Font.FuturaMedium>
                        </>
                    )}
                </div>
                {!isAuthenticated && (
                    <div className={styles.scoreDetail}>
                        <button type="button" className={styles.loginButton} onClick={login}>
                            <Font.OxaniumRegular desktop="14" mobile="12">
                                {t('singleRunner.resultsTable.connectSeeIndex')}
                            </Font.OxaniumRegular>
                        </button>
                    </div>
                )}
                {canDelete && (
                    <div className={styles.delete}>
                        <button type="button" className={styles.deleteButton} onClick={() => onDelete!(result.uri)} aria-label="Supprimer ce résultat">
                            <Icon variant={IconsEnum.TRASH} width={24} height={24} />
                        </button>
                    </div>
                )}
            </div>

            {/* Mobile-only popover for the stages detail (desktop uses the hover tooltip above).
                Absolute against .palmaresRow, anchored just below the "Type" line. */}
            {showStagesTag && stagesPopoverOpen && (
                <div className={styles.stagesPopoverMobile} role="dialog" style={stagesPos ? { top: stagesPos.top, left: stagesPos.left } : undefined}>
                    <button type="button" className={styles.stagesPopoverClose} onClick={() => setStagesPopoverOpen(false)} aria-label={t('close')}>
                        <Icon variant={IconsEnum.CLOSE} width={16} height={16} />
                    </button>
                    <StagesTable stages={stages} />
                </div>
            )}
        </div>
    );
}
